import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const SearchAutocomplete = ({
  value,
  onChange,
  onSearch,
  suggestions = [],
  loading = false,
  placeholder,
  className = '',
  inputClassName = '',
  minChars = 1,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const listId = useId();
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const trimmed = String(value ?? '').trim();
  const showPanel = open && trimmed.length >= minChars;

  useEffect(() => {
    setActiveIndex(-1);
  }, [suggestions, value]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const commit = (next) => {
    if (next && typeof next === 'object') {
      const selected = String(next.label ?? next.value ?? '').trim();
      onChange(selected);
      // Pass the suggestion object so callers that need ids (e.g. staff picker) still work.
      // Callers that only need text must coerce with String(...).
      onSearch(next);
      setOpen(false);
      setActiveIndex(-1);
      inputRef.current?.blur();
      return;
    }
    const v = String(next ?? value ?? '').trim();
    onChange(v);
    onSearch(v);
    setOpen(false);
    setActiveIndex(-1);
    inputRef.current?.blur();
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      setOpen(false);
      setActiveIndex(-1);
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (activeIndex >= 0 && suggestions[activeIndex]) {
        commit(suggestions[activeIndex]);
      } else {
        commit();
      }
      return;
    }
    if (!showPanel || !suggestions.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    }
  };

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      <div className="relative">
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" aria-hidden>
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M11 18a7 7 0 100-14 7 7 0 000 14z" />
          </svg>
        </span>
        <input
          ref={inputRef}
          type="search"
          value={value}
          disabled={disabled}
          onChange={(e) => {
            onChange(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={placeholder || t('common.searchPlaceholder')}
          role="combobox"
          aria-expanded={showPanel}
          aria-controls={listId}
          aria-autocomplete="list"
          autoComplete="off"
          className={`w-full rounded-lg border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 py-2 pl-9 pr-9 text-sm outline-none focus:border-glow-500 ${inputClassName}`}
        />
        {value && (
          <button
            type="button"
            onClick={() => {
              onChange('');
              onSearch('');
              setOpen(false);
              inputRef.current?.focus();
            }}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-zinc-500 hover:bg-zinc-200 dark:hover:bg-zinc-800"
            aria-label={t('common.clearSearch')}
          >
            ×
          </button>
        )}
      </div>

      {showPanel && (
        <ul
          id={listId}
          role="listbox"
          className="absolute z-50 mt-1 max-h-72 w-full overflow-auto rounded-xl border border-zinc-200 bg-white py-1 shadow-lg dark:border-zinc-700 dark:bg-zinc-900"
        >
          {loading && (
            <li className="px-4 py-2.5 text-sm text-zinc-500">{t('common.loadingSuggestions')}</li>
          )}
          {!loading && suggestions.length === 0 && (
            <li className="px-4 py-2.5 text-sm text-zinc-500">{t('common.noSearchSuggestions')}</li>
          )}
          {!loading &&
            suggestions.map((item, i) => (
              <li key={`${item.value ?? item.label}-${i}`} role="option" aria-selected={i === activeIndex}>
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => commit(item)}
                  className={`flex w-full flex-col px-4 py-2.5 text-left text-sm hover:bg-zinc-100 dark:hover:bg-zinc-800 ${
                    i === activeIndex ? 'bg-zinc-100 dark:bg-zinc-800' : ''
                  }`}
                >
                  <span className="font-medium text-zinc-800 dark:text-zinc-100">{item.label}</span>
                  {item.hint && (
                    <span className="mt-0.5 text-xs text-zinc-500">{item.hint}</span>
                  )}
                </button>
              </li>
            ))}
        </ul>
      )}
    </div>
  );
};

export default SearchAutocomplete;
